import { FC, useEffect, useState } from 'react';
import PaginationBar from '../../components/PaginationBar';
import { apiFetch } from '../../utils/api';

interface Bay {
  id: string;
  name: string;
  locationId: string | null;
  status: 'active' | 'inactive';
  location?: { id: string; name: string } | null;
}

interface LocationLite { id: string; name: string }

async function fetchBays(params: { page?: number; limit?: number; search?: string; locationId?: string; status?: string }) {
  const sp = new URLSearchParams();
  if (params.page) sp.set('page', String(params.page));
  if (params.limit) sp.set('limit', String(params.limit));
  if (params.search) sp.set('search', params.search);
  if (params.locationId) sp.set('locationId', params.locationId);
  if (params.status) sp.set('status', params.status);
  const q = sp.toString();
  return apiFetch<{ data: Bay[]; meta: { total: number; page: number; limit: number; totalPages: number } }>(`/bays${q ? `?${q}` : ''}`);
}

async function createBay(input: Partial<Bay>): Promise<Bay> {
  return apiFetch<Bay>('/bays', { method: 'POST', body: JSON.stringify(input) });
}

async function updateBay(id: string, input: Partial<Bay>) {
  return apiFetch<Bay>(`/bays/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
}

const BaysPanel: FC = () => {
  const [rows, setRows] = useState<Bay[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');

  // add form
  const [name, setName] = useState('');
  const [locationId, setLocationId] = useState('');
  const [status, setStatus] = useState<'active' | 'inactive'>('active');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // locations
  const [locations, setLocations] = useState<LocationLite[]>([]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetchBays({ page, limit: 20, search: search.trim() || undefined, locationId: locationFilter || undefined, status: statusFilter || undefined });
      setRows(resp.data);
      setTotalPages(resp.meta.totalPages);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load bays');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, [page, search, locationFilter, statusFilter]);

  useEffect(() => {
    let ignore = false;
    apiFetch<{ data: any[] }>(`/locations?limit=100`).then((resp) => {
      if (ignore) return;
      const list = (resp.data ?? []).map((l: any) => ({ id: l.id, name: l.name ?? l.code ?? 'Location' }));
      setLocations(list);
      if (!locationId && list.length) setLocationId(list[0].id);
    }).catch(() => { if (!ignore) setLocations([]); });
    return () => { ignore = true; };
  }, [locationId]);

  const onAdd = async () => {
    if (creating) return;
    if (!name.trim() || !locationId) {
      setCreateError('Please enter name and choose a location.');
      return;
    }
    setCreateError(null);
    setCreating(true);
    try {
      await createBay({ name: name.trim(), status, locationId });
      setName('');
      setStatus('active');
      await load();
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : 'Failed to create bay');
    } finally { setCreating(false); }
  };

  return (
    <div className="rounded-2xl bg-white px-6 py-5 shadow-inner">
      {/* Filters (like models screen) */}
      <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="search"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search bays"
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 sm:w-64"
            />
            <select
              value={locationFilter}
              onChange={(e) => { setLocationFilter(e.target.value); setPage(1); }}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 min-w-[12rem]"
            >
              <option value="">All locations</option>
              {locations.map((l) => (<option key={l.id} value={l.id}>{l.name}</option>))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              <option value="">All status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            {(locationFilter || statusFilter) && (
              <button
                type="button"
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-primary/30 hover:text-primary shadow-sm"
                onClick={() => {
                  setSearch('');
                  setLocationFilter('');
                  setStatusFilter('');
                  setPage(1);
                }}
              >
                <span>✕</span> Clear Filter
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Add new bay (like models create panel) */}
      <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/60 p-4 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Bay name (e.g., Bay 1)"
            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 sm:w-64"
            disabled={creating}
          />
          <select
            value={locationId}
            onChange={(e) => setLocationId(e.target.value)}
            className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 min-w-[12rem]"
            disabled={creating}
          >
            {locations.map((l) => (<option key={l.id} value={l.id}>{l.name}</option>))}
          </select>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as any)}
            className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
            disabled={creating}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <button
            onClick={onAdd}
            className="inline-flex items-center justify-center rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-70"
            disabled={creating}
          >
            {creating ? 'Saving…' : '+ Add'}
          </button>
        </div>
        {createError ? <p className="mt-2 text-sm text-danger">{createError}</p> : null}
      </div>

      {/* Table */}
      <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200">
        <table className="min-w-full text-left text-sm text-slate-600">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-5 py-3 font-semibold">Name</th>
              <th className="px-5 py-3 font-semibold">Location</th>
              <th className="px-5 py-3 font-semibold">Status</th>
              <th className="px-5 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className="px-5 py-6 text-center text-slate-400" colSpan={4}>Loading bays…</td>
              </tr>
            ) : error ? (
              <tr>
                <td className="px-5 py-6 text-center text-danger" colSpan={4}>{error}</td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td className="px-5 py-6 text-center text-slate-400" colSpan={4}>No bays found.</td>
              </tr>
            ) : (
              rows.map((b) => (
                <BayRow key={b.id} bay={b} locations={locations} onSaved={load} />
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4">
        <PaginationBar
          page={page}
          totalPages={totalPages}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
          onSelect={(v) => setPage(v)}
          disabled={loading || totalPages === 0}
          renderSummary={<span>{rows.length ? `Page ${page} of ${totalPages}` : 'No bays found'}</span>}
        />
      </div>
    </div>
  );
};

export default BaysPanel;

const BayRow: FC<{ bay: Bay; locations: LocationLite[]; onSaved: () => void }> = ({ bay, locations, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(bay.name);
  const [status, setStatus] = useState<'active' | 'inactive'>(bay.status);
  const [locationId, setLocationId] = useState<string>(bay.location?.id ?? bay.locationId ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await updateBay(bay.id, { name: name.trim(), status, locationId });
      setEditing(false);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <tr className="even:bg-slate-50/60">
      <td className="px-5 py-3 text-slate-700 font-semibold">
        {editing ? (
          <input value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" disabled={saving} />
        ) : (
          bay.name
        )}
        {error ? <div className="mt-1 text-xs text-danger">{error}</div> : null}
      </td>
      <td className="px-5 py-3 text-slate-600">
        {editing ? (
          <select value={locationId} onChange={(e) => setLocationId(e.target.value)} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" disabled={saving}>
            {locations.map((l) => (<option key={l.id} value={l.id}>{l.name}</option>))}
          </select>
        ) : (
          bay.location?.name ?? '—'
        )}
      </td>
      <td className="px-5 py-3 text-slate-600 capitalize">
        {editing ? (
          <select value={status} onChange={(e) => setStatus(e.target.value as any)} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" disabled={saving}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        ) : (
          bay.status
        )}
      </td>
      <td className="px-5 py-3 text-right">
        {editing ? (
          <div className="inline-flex gap-2">
            <button onClick={() => setEditing(false)} disabled={saving} className="rounded-lg border border-slate-300 px-3 py-1 text-xs">Cancel</button>
            <button onClick={handleSave} disabled={saving} className="rounded-lg border border-primary px-3 py-1 text-xs text-primary">{saving ? 'Saving…' : 'Save'}</button>
          </div>
        ) : (
          <button onClick={() => setEditing(true)} className="rounded-lg border border-slate-300 px-3 py-1 text-xs">Edit</button>
        )}
      </td>
    </tr>
  );
};
